import React from 'react'
import type { LotteryRound } from '../services/lotteryService'

interface HuayLiveBadgeProps<T = LotteryRound> {
  round: T
  onOpen: (round: T) => void
  /** Visual size, default: small */
  size?: 'sm' | 'md'
  /** Position style, default: top-right corner of the parent (parent must be position: relative) */
  position?: { top?: number | string; right?: number | string; bottom?: number | string; left?: number | string }
}

/** Floating LIVE badge overlay for a lottery card. Click opens an embedded YouTube player. */
function HuayLiveBadgeBase<T = LotteryRound>({ round, onOpen, size = 'sm', position }: HuayLiveBadgeProps<T>) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    onOpen(round)
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      onOpen(round)
    }
  }
  const padX = size === 'md' ? 8 : 6
  const padY = size === 'md' ? 3 : 2
  const fontSize = size === 'md' ? 11 : 9
  const dot = size === 'md' ? 7 : 6
  const pos = {
    top: position?.top ?? 4,
    right: position?.right ?? 4,
    ...(position?.bottom !== undefined && { bottom: position.bottom }),
    ...(position?.left !== undefined && { left: position.left }),
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label="ดูถ่ายทอดสดหวยนี้"
      style={{
        position: 'absolute',
        top: pos.top,
        right: pos.right,
        zIndex: 5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: 999,
        padding: `${padY}px ${padX}px`,
        fontSize,
        fontWeight: 800,
        letterSpacing: 0.4,
        boxShadow: '0 6px 14px rgba(239, 68, 68, 0.45)',
        cursor: 'pointer',
        lineHeight: 1,
      }}
    >
      <span
        style={{
          width: dot,
          height: dot,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 0 0 0 rgba(255,255,255,0.7)',
          animation: 'huayLiveBadgePulse 1.4s infinite',
        }}
      />
      LIVE
      <style>{`
        @keyframes huayLiveBadgePulse {
          0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
          70% { box-shadow: 0 0 0 6px rgba(255,255,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
        }
      `}</style>
    </button>
  )
}

export const HuayLiveBadge = HuayLiveBadgeBase as <T = LotteryRound>(props: HuayLiveBadgeProps<T>) => React.ReactElement
export default HuayLiveBadge
